import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Subject } from 'rxjs';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './cost-review-modal.module.less';
import { messages } from './messages';
import { RateShopperAudit } from './rate-shopper-audit';
import { useReloadCostReviewAfterRecommendationsApplied } from './use-reload-cost-review-after-recommendations-applied';
import CostReviewLineItemInsurance from '../../../components/cost-review-line-item/cost-review-line-item-insurance';
import { UpsTermsCheckbox } from '../../ups-terms-checkbox/ups-terms-checkbox';
import { useEnableUpsTerms2 } from '../../../feature-flags/hooks/use-enable-ups-terms-2';
import { emptyArray } from 'ui-components/default-props/empty';
import { hasUpsProviderInCostReview } from '../../../store/quickship/quickship.helpers';
import { usePaymentSurchargePreview } from '../../../financial-services-ui/surcharge/hooks';
import { useCurrentCostReviewTrackingId } from '../../../fulfillment-planner/hooks/useCurrentCostReviewTrackingId';
import { useBalancePurchase } from '../../../hooks/cost-review/use-balance-purchase';
import { useCombinedCostInfo } from '../../../hooks/cost-review/use-combined-cost-info';
import { useCostReviewRecommendations } from '../../../hooks/cost-review/use-cost-review-recommendations';
import { useEntitiesFulfillmentPlanProperty } from '../../../hooks/entities/selectors/fulfillment-plans';
import { getCostReview } from '../../../pages/settings/api/service-collections';
import { updateSellerSettingsBulk } from '../../../pages/settings/api/save-settings';
import { LogService } from '../../../services/log-service';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING } from '../../../wallet/utilities/constants';
import { Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { CostReviewModalFooter } from '../../cost-review/cost-review-modal-footer/cost-review-modal-footer';
import { CostReviewRecommendationBanner } from '../../cost-review/cost-review-recommendation-banner/cost-review-recommendation-banner';
import { CostReviewLabelProviderSection } from '../../cost-review-line-item/cost-review-label-provider-section';
import { WalletCostReviewSection } from '../../walleted-cost-review-line-item/wallet-cost-review-section';

/**
 * @typedef {import('../../../prop-types/cost-review.types').LabelProviderCostReview} LabelProviderCostReview
 * @typedef {import('../../../prop-types/cost-review.types').WalletedProviderCostReview} WalletedProviderCostReview
 * @typedef {import('../../../prop-types/cost-review.types').InsuranceProviderCostReview} InsuranceProviderCostReview
 * @typedef {import('../../../prop-types/cost-review.types').CostReviewWarning} CostReviewWarning
 */

const MODAL_HEADER_ID = 'cost-review-modal-header';

export const costReviewSubject = new Subject();

/**
 * @param {object} props
 * @param {LabelProviderCostReview[]} props.providers
 * @param {WalletedProviderCostReview[][]} props.walletedProviderGroups
 * @param {InsuranceProviderCostReview[]} props.insurances
 * @param {(walletId: string, amount: number) => void} props.walletPurchaseAmountChanged
 * @param {(provider: string, amount: number) => void} props.providerPurchaseAmountChanged
 * @param {(provider: string, amount: number) => void} props.insurancePurchaseAmountChanged
 * @param {boolean} props.isContinueDisabled
 * @param {ModalFrameworkMethodsPropType} props.modalFrameworkMethods
 * @param {(providers: LabelProviderCostReview[], insurances: InsuranceProviderCostReview[], walletedProviderGroups: WalletedProviderCostReview[][]) => void} props.purchasePostage
 * @param {() => void} props.deferPostagePurchase
 * @param {boolean} props.enoughFunds
 * @param {string} props.shipDate
 * @param {(newShipDate: string) => void} props.onShipDateChanged
 * @param {boolean} props.userHasBillingInfoSetup
 * @param {() => void} props.closeOrderDetailsModal
 * @param {string} props.userDateFormat
 * @param {Array<CostReviewWarning>} props.costWarnings
 * @param {boolean} props.readOnlyCostReviewModal
 * @param {string[]} props.fulfillmentPlanIds
 * @param {boolean} props.isLoadingCostReview
 * @param {boolean} props.isReloadCostReviewDisabled
 * @param {BulkRateRecommendationSummary} props.recommendationSummary
 * @param {(bulkUpdateJobGuid: string, serviceRecommendationsToApply: Array<BulkApplyRateRecommendationsServiceFilter>, insuranceRecommendationsToApply: Array<BulkApplyRateRecommendationsInsuranceFilter>) => void} props.applyRecommendations
 * @param {boolean} props.isApplyingRecommendations
 * @param {Currency} props.totalAppliedSavings
 * @param {boolean} props.upsTermsRequired
 * @returns {React.JSX.Element}
 */
export const CostReviewModal = ({
  providers = emptyArray,
  walletedProviderGroups = emptyArray,
  insurances = emptyArray,
  walletPurchaseAmountChanged = noop,
  providerPurchaseAmountChanged = noop,
  insurancePurchaseAmountChanged = noop,
  isContinueDisabled = true,
  modalFrameworkMethods,
  purchasePostage: purchasePostageProp = noop,
  deferPostagePurchase = noop,
  enoughFunds = false,
  shipDate,
  onShipDateChanged = noop,
  userHasBillingInfoSetup = false,
  closeOrderDetailsModal = noop,
  userDateFormat = 'MM/DD/YYYY',
  costWarnings = emptyArray,
  readOnlyCostReviewModal = false,
  fulfillmentPlanIds,
  isLoadingCostReview,
  isReloadCostReviewDisabled,
  recommendationSummary,
  applyRecommendations,
  isApplyingRecommendations,
  totalAppliedSavings,
  upsTermsRequired = false,
}) => {
  const dispatch = useDispatch();
  const [error, setError] = useState(undefined);
  const [upsTermsAccepted, setUpsTermsAccepted] = useState(false);
  const isUpsTermsEnabled = useEnableUpsTerms2();
  const isCancel = useRef(true);
  const trackAddFunds = useTrackWorkflow(Workflows.ADD_FUNDS_FLOW);
  const [disableButton, setDisableButton] = useState(false);
  const fulfillmentPlanId = fulfillmentPlanIds?.length === 1 ? fulfillmentPlanIds[0] : undefined;
  const serviceCollectionGuid = useEntitiesFulfillmentPlanProperty(fulfillmentPlanId, 'serviceCollectionGuid');
  const hasRateShopperAssigned = !!serviceCollectionGuid && serviceCollectionGuid.length > 0;
  const [rateShopperAudit, setRateShopperAudit] = useAsyncSafeState(null);

  useEffect(() => {
    if (fulfillmentPlanId && hasRateShopperAssigned) {
      getCostReview(fulfillmentPlanId)
        .then((response) => {
          const { services } = response.data;
          if (!services || services.length === 0) {
            return; // if you dont have services hide the audit
          }
          setRateShopperAudit(response.data);
        })
        .catch((er) => LogService.logError(new Error('Error fetching rate shopper data', { cause: er })));
    }
  }, [fulfillmentPlanId, hasRateShopperAssigned, setRateShopperAudit]);

  const onAddFundsComplete = () => {
    isCancel.current = false;
  };

  // CostReview Subject
  useEffect(() => {
    const subscription = costReviewSubject.subscribe(({ costReviewErrorMessage }) => {
      const firstErrorMessage = costReviewErrorMessage.length === 0 ? undefined : costReviewErrorMessage[0].errorMessage;
      setError(firstErrorMessage);
    });
    return () => subscription.unsubscribe();
  }, []);
  const { closeModal } = modalFrameworkMethods;
  const { currentAnalyticsSourceTrackingId } = useCurrentCostReviewTrackingId();

  // This is intended to only ever run once because we want to track the start of the add funds process.
  // The clean-up function will track the cancel event. This should only be modified with caution.

  useEffect(() => {
    if (!readOnlyCostReviewModal && currentAnalyticsSourceTrackingId && !enoughFunds) {
      trackAddFunds(WorkflowSteps.START, {
        source: currentAnalyticsSourceTrackingId,
      });
    }
    return () => {
      if (!readOnlyCostReviewModal && currentAnalyticsSourceTrackingId && isCancel.current && !enoughFunds) {
        trackAddFunds(WorkflowSteps.CANCEL);
      }
    };
  }, [currentAnalyticsSourceTrackingId, enoughFunds, readOnlyCostReviewModal, trackAddFunds]);

  const combinedCostInfo = useCombinedCostInfo(providers, insurances, walletedProviderGroups);
  const { totalWalletPurchaseAmount } = combinedCostInfo;

  const hasUpsProviderInCosts = useMemo(
    () => hasUpsProviderInCostReview(providers, walletedProviderGroups),
    [providers, walletedProviderGroups],
  );
  const {
    loading: surchargeLoading,
    creditCardProcessingFee,
    creditCardProcessingFeePercentage,
    error: previewError,
    triggerRetry: triggerPreviewRetry,
    hasCheckedAtLeastOnce: hasSurchargeCheckedAtLeastOnce,
  } = usePaymentSurchargePreview({ total: totalWalletPurchaseAmount });
  const transactionPreviewProps = useMemo(
    () => ({
      surchargeLoading,
      creditCardProcessingFee,
      creditCardProcessingFeePercentage,
      previewError,
      triggerPreviewRetry,
      hasSurchargeCheckedAtLeastOnce,
    }),
    [
      surchargeLoading,
      creditCardProcessingFee,
      creditCardProcessingFeePercentage,
      previewError,
      triggerPreviewRetry,
      hasSurchargeCheckedAtLeastOnce,
    ],
  );

  const purchasePostage = useBalancePurchase(purchasePostageProp, trackAddFunds, onAddFundsComplete, closeModal);

  const proceedWithPurchase = useCallback(() => {
    if (enoughFunds) {
      deferPostagePurchase();
      closeModal();
      return;
    }
    purchasePostage(providers, insurances, walletedProviderGroups);
  }, [closeModal, deferPostagePurchase, enoughFunds, insurances, providers, purchasePostage, walletedProviderGroups]);

  const onClick = useCallback(async () => {
    setDisableButton(true);
    setError(undefined);

    if (upsTermsRequired && upsTermsAccepted) {
      try {
        const newSettings = await updateSellerSettingsBulk({
          [HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING]: 'true',
        });
        dispatch(saveNewSellerSettings(newSettings));
      } catch {
        setDisableButton(false);
        setError('Failed to save UPS terms acceptance. Please try again.');
        return;
      }
    }

    proceedWithPurchase();
  }, [dispatch, proceedWithPurchase, upsTermsRequired, upsTermsAccepted]);

  const { isWaitingToReloadCostReview } = useReloadCostReviewAfterRecommendationsApplied(
    Boolean(totalAppliedSavings?.value),
    isReloadCostReviewDisabled,
  );

  const {
    shippingServiceRecommendations,
    selectedShippingServiceRecommendations,
    toggleShippingServiceRecommendation,
    selectedInsuranceRecommendations,
    insuranceRecommendations,
    toggleInsuranceRecommendation,
    selectedRateShopperRecommendations,
    rateShopperRecommendations,
    toggleRateShopperRecommendation,
    selectAllRecommendations,
    hasSelectedRecommendations,
    applySelectedRecommendations,
  } = useCostReviewRecommendations(recommendationSummary, applyRecommendations);

  const isToggleRecommendationDisabled = Boolean(isApplyingRecommendations || totalAppliedSavings?.value);
  const hasRecommendations = Boolean(
    Object.values(insuranceRecommendations).length ||
    Object.values(shippingServiceRecommendations).length ||
    Object.values(rateShopperRecommendations).length,
  );
  const shouldDisableContinue = Boolean(
    disableButton ||
    isContinueDisabled ||
    surchargeLoading ||
    previewError ||
    (isUpsTermsEnabled && upsTermsRequired && hasUpsProviderInCosts && !upsTermsAccepted),
  );

  const applyRecommendationsButton = (
    <Button disabled={!hasSelectedRecommendations || isToggleRecommendationDisabled} onClick={applySelectedRecommendations} theme='primary'>
      <FormattedMessage {...messages.applySavings} />
    </Button>
  );

  const showSpinner = isLoadingCostReview || isWaitingToReloadCostReview;

  return (
    <Modal className={style['cost-review-modal']} modalHeaderId={MODAL_HEADER_ID}>
      <ModalHeader closeModal={closeModal} id={MODAL_HEADER_ID}>
        <FormattedMessage {...messages.header} />
      </ModalHeader>
      <ModalBody className={style['modal-body']} showLoading={showSpinner}>
        {error ? <BannerMessageV2 content={error} status={BannerStatus.Error} /> : null}
        <CostReviewRecommendationBanner
          isApplyingRecommendations={isApplyingRecommendations}
          selectAllRecommendations={selectAllRecommendations}
          totalAppliedSavings={totalAppliedSavings}
          totalRecommendationSavings={recommendationSummary?.totalSavings}
        />
        {walletedProviderGroups.map((walletProviderGroup) => (
          <WalletCostReviewSection
            key={walletProviderGroup[0].walletId}
            costWarnings={costWarnings}
            insuranceRecommendations={insuranceRecommendations}
            isToggleRecommendationDisabled={isToggleRecommendationDisabled}
            onPurchaseAmountChange={walletPurchaseAmountChanged}
            onToggleRateShopperRecommendation={toggleRateShopperRecommendation}
            onToggleShippingServiceRecommendation={toggleShippingServiceRecommendation}
            rateShopperRecommendations={rateShopperRecommendations}
            readOnlyCostReviewModal={readOnlyCostReviewModal}
            selectedInsuranceRecommendations={selectedInsuranceRecommendations}
            selectedRateShopperRecommendations={selectedRateShopperRecommendations}
            selectedShippingServiceRecommendations={selectedShippingServiceRecommendations}
            shippingServiceRecommendations={shippingServiceRecommendations}
            toggleInsuranceRecommendation={toggleInsuranceRecommendation}
            walletProviderGroup={walletProviderGroup}
          />
        ))}
        {providers.map((provider, index) => {
          const insuranceRecommendation = insuranceRecommendations[provider.sellerLabelProviderId];
          const isInsuranceRecommendationSelected = Boolean(selectedInsuranceRecommendations[insuranceRecommendation?.key]);

          return (
            <CostReviewLabelProviderSection
              key={index}
              costReviewLineItemProvider={provider}
              costWarnings={costWarnings}
              insuranceRecommendation={insuranceRecommendation}
              isInsuranceRecommendationSelected={isInsuranceRecommendationSelected}
              isToggleRecommendationDisabled={isToggleRecommendationDisabled}
              onPurchaseAmountChange={providerPurchaseAmountChanged}
              onToggleRateShopperRecommendation={toggleRateShopperRecommendation}
              onToggleShippingServiceRecommendation={toggleShippingServiceRecommendation}
              rateShopperRecommendations={rateShopperRecommendations[provider.sellerLabelProviderId]}
              readOnlyCostReviewModal={readOnlyCostReviewModal}
              selectedRateShopperRecommendations={selectedRateShopperRecommendations}
              selectedShippingServiceRecommendations={selectedShippingServiceRecommendations}
              shippingServiceRecommendations={shippingServiceRecommendations[provider.sellerLabelProviderId]}
              toggleInsuranceRecommendation={toggleInsuranceRecommendation}
            />
          );
        })}
        {insurances.map((insurance, index) => (
          <CostReviewLineItemInsurance
            key={index}
            closeModal={closeModal}
            closeOrderDetailsModal={closeOrderDetailsModal}
            insuranceProvider={insurance}
            onPurchaseAmountChange={insurancePurchaseAmountChanged}
            readOnlyCostReviewModal={readOnlyCostReviewModal}
            userHasBillingInfoSetup={userHasBillingInfoSetup}
          />
        ))}
        {rateShopperAudit ? <RateShopperAudit closeModal={closeModal} rateShopperAudit={rateShopperAudit} /> : null}
        {isUpsTermsEnabled && upsTermsRequired && hasUpsProviderInCosts && !readOnlyCostReviewModal ? (
          <UpsTermsCheckbox checked={upsTermsAccepted} onChange={setUpsTermsAccepted} />
        ) : null}
      </ModalBody>
      {!showSpinner && (
        <CostReviewModalFooter
          combinedCostInfo={combinedCostInfo}
          creditCardProcessingFee={creditCardProcessingFee}
          creditCardProcessingFeePercentage={creditCardProcessingFeePercentage}
          fulfillmentPlanIds={fulfillmentPlanIds}
          isSummaryReadonly={readOnlyCostReviewModal}
          onShipDateChanged={onShipDateChanged}
          shipDate={shipDate}
          transactionPreviewProps={transactionPreviewProps}
          userDateFormat={userDateFormat}
        >
          {readOnlyCostReviewModal ? (
            <>
              <Button onClick={closeModal} theme='tertiary'>
                <FormattedMessage {...messages.close} />
              </Button>
              {hasRecommendations ? applyRecommendationsButton : null}
            </>
          ) : (
            <>
              <Button onClick={() => closeModal(true)} theme='tertiary'>
                <FormattedMessage {...messages.cancel} />
              </Button>
              {hasSelectedRecommendations ? (
                applyRecommendationsButton
              ) : (
                <Button disabled={shouldDisableContinue} onClick={onClick} theme='primary'>
                  <FormattedMessage {...messages.continue} />
                </Button>
              )}
            </>
          )}
        </CostReviewModalFooter>
      )}
    </Modal>
  );
};
